import { formatDate, DATE_FORMATS } from '@frontend/mwm-ui'
import { MdEdit } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { ASSET_STATUS, IAssetTag } from '~/types/assets-management'

import { AssetStatusBadge } from '~/components/Badges'
import Button from '~/components/Buttons/Button'
import DoubleBadge from '~/components/DoubleBadge'
import Tooltip from '~/components/Tooltip'
import UserCard from '~/components/UserCard'
import { getSingleValueTags } from '~/utils/asset'

import './styles.scss'

type DateCardInfo = {
  date: number
  name: string
  picture?: string
}

interface Props {
  createdBy?: DateCardInfo
  expirationDate: number | null
  onEditAsset: VoidFunction
  status: ASSET_STATUS
  tags?: IAssetTag[]
  title: string
  type: string
  updatedBy?: DateCardInfo
}

const MAX_TAGS_ITEM = 4

const AssetDetailsGeneralInformation = ({
  createdBy,
  expirationDate,
  onEditAsset,
  status,
  tags,
  title,
  type,
  updatedBy,
}: Props): ReactElement => {
  const getTags = (): IAssetTag[] => {
    if (!tags) return []

    return getSingleValueTags(tags)
  }

  return (
    <div className="asset-details-general-information-container">
      <div className="flex-row-container">
        <div className="title-container">
          <div className="title-container__title">{title}</div>
          <AssetStatusBadge size="large" value={status} />
        </div>
        <Button onClick={onEditAsset} outlined size="small">
          <MdEdit color="#ff5301" size={9.75} />
          Edit
        </Button>
      </div>
      <div className="flex-row-container">
        <div className="column">
          <div className="text-content">
            Type <span className="text-content__value">{type}</span>
          </div>
          <div className="text-content">
            Expiration date{' '}
            <span className="text-content__value">{expirationDate ? formatDate(expirationDate) : '--'}</span>
          </div>
        </div>
        <div className="column">
          <div className="column__title">Tags</div>
          <div className="tags-container">
            {getTags()
              .slice(0, MAX_TAGS_ITEM)
              .map((tag: IAssetTag, index) => (
                <DoubleBadge key={`${tag.key}-${tag.value[0]}-${index}`} keyTag={tag.key} valueTag={tag.value[0]} />
              ))}
            {getTags().length > MAX_TAGS_ITEM ? (
              <Tooltip
                align={{ points: ['br', 'tr'] }}
                className="assets-management-tags-tooltip"
                overlay={
                  <div className="badges-wrapper">
                    {getTags()
                      .slice(MAX_TAGS_ITEM)
                      .map((tag: IAssetTag, index) => (
                        <DoubleBadge
                          key={`${tag.key}-${tag.value[0]}-${index}`}
                          keyTag={tag.key}
                          valueTag={tag.value[0]}
                        />
                      ))}
                  </div>
                }
                placement="top"
                trigger="hover"
              >
                <span className="asset-tag-count">{`+${getTags().length - MAX_TAGS_ITEM}`} </span>
              </Tooltip>
            ) : null}
          </div>
        </div>
        <div className="column">
          {createdBy ? (
            <UserCard
              label="Created by"
              name={createdBy.name}
              picture={createdBy.picture}
              subInfo={formatDate(createdBy.date, DATE_FORMATS.DEFAULT_WITH_HOURS)}
            />
          ) : null}
        </div>
        <div className="column">
          {updatedBy ? (
            <UserCard
              label="Last update by"
              name={updatedBy.name}
              picture={updatedBy.picture}
              subInfo={formatDate(updatedBy.date, DATE_FORMATS.DEFAULT_WITH_HOURS)}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default AssetDetailsGeneralInformation
